import React, { useState, useCallback } from 'react';
import {
  Type,
  Heading,
  Image,
  Video,
  ListVideo,
  Music,
  CreditCard,
  FileText,
  Code,
  Minus,
  List,
  AlertCircle,
  CheckSquare,
  AlertTriangle,
  Layers,
  LayoutGrid,
  Quote,
  Library,
  BookOpen,
  Link,
  GanttChartSquare,
  FileCode2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsRight,
  Plus,
  Columns,
  Square,
  Circle,
  Triangle,
  Star,
  Hexagon,
  PenTool,
  Sparkles,
} from 'lucide-react';

// ---- Content block tools (add blocks to current slide) ----
// `span` controls how many grid columns a card occupies (2 = wide card).
const CONTENT_TOOLS = [
  { id: 'add-text', label: 'Text', icon: Type, action: 'text', color: 'text-slate-400', description: 'Add a rich text block' },
  { id: 'add-headline', label: 'Heading', icon: Heading, action: 'headline', color: 'text-slate-400', description: 'Add a heading' },
  { id: 'add-list', label: 'List', icon: List, action: 'list', color: 'text-slate-400', description: 'Add a list block' },
  { id: 'add-apa-quote', label: 'APA Quote', icon: Quote, action: 'apa-quote', color: 'text-rose-400', description: 'Add an APA block quote' },
  { id: 'add-citation', label: 'Citation', icon: Library, action: 'apa-citation', color: 'text-indigo-400', description: 'Add an APA citation' },
  { id: 'add-reference-list', label: 'Reference List', icon: BookOpen, action: 'apa-reference-list', color: 'text-blue-400', description: 'Add an APA reference list' },
  { id: 'add-link', label: 'Link', icon: Link, action: 'link', color: 'text-sky-400', description: 'Add a link block' },
  { id: 'add-image', label: 'Image', icon: Image, action: 'image', color: 'text-green-400', description: 'Add an image' },
  { id: 'add-video', label: 'Video', icon: Video, action: 'video', color: 'text-red-400', description: 'Add a video' },
  { id: 'add-playlist', label: 'Playlist', icon: ListVideo, action: 'youtube-playlist', color: 'text-red-400', description: 'Add a YouTube playlist card' },
  { id: 'add-audio', label: 'Audio', icon: Music, action: 'audio', color: 'text-purple-400', description: 'Add audio' },
  { id: 'add-cards', label: 'Cards', icon: CreditCard, action: 'cards', color: 'text-yellow-400', description: 'Add a card layout' },
  { id: 'add-stacked', label: 'Stack', icon: Layers, action: 'stacked-cards', color: 'text-orange-400', description: 'Add stacked cards' },
  { id: 'add-columns', label: 'Columns', icon: Columns, action: 'multi-column-cards', color: 'text-cyan-400', description: 'Add multi-column layout' },
  { id: 'add-timeline', label: 'Timeline', icon: GanttChartSquare, action: 'project-timeline', color: 'text-teal-400', description: 'Add a project timeline' },
  { id: 'add-divider', label: 'Divider', icon: Minus, action: 'divider', color: 'text-slate-500', description: 'Add a divider line' },
  { id: 'add-html', label: 'Code', icon: Code, action: 'html', color: 'text-emerald-400', description: 'Add a code block' },
  { id: 'add-worksheet', label: 'Sheet', icon: FileText, action: 'worksheet', color: 'text-indigo-400', description: 'Add a worksheet' },
  {
    id: 'add-custom-html',
    label: 'HTML/CSS',
    icon: FileCode2,
    action: 'custom-html',
    color: 'text-fuchsia-400',
    span: 2,
    description: 'Lay this slide out with your own HTML and CSS',
  },
];

const CALLOUT_TOOLS = [
  { id: 'add-info-box', label: 'Info', icon: AlertCircle, action: 'info-box', color: 'text-blue-400', description: 'Add info callout' },
  { id: 'add-exercise-box', label: 'Exercise', icon: CheckSquare, action: 'exercise-box', color: 'text-emerald-400', description: 'Add exercise callout' },
  { id: 'add-warning-box', label: 'Warning', icon: AlertTriangle, action: 'warning-box', color: 'text-amber-400', description: 'Add warning callout' },
];

const PRESET_TOOLS = [
  { id: 'preset-single-column', label: '1-Col', icon: Square, presetKey: 'single-column', description: 'Single column layout' },
  { id: 'preset-two-columns', label: '2-Col', icon: Columns, presetKey: 'two-columns', description: 'Two column layout' },
  { id: 'preset-three-columns', label: '3-Col', icon: LayoutGrid, presetKey: 'three-columns', description: 'Three column layout' },
  { id: 'preset-image-text', label: 'Img+Txt', icon: Image, presetKey: 'image-and-text', description: 'Image and text layout' },
  { id: 'preset-cards-grid', label: 'Grid', icon: CreditCard, presetKey: 'cards-grid', description: 'Card grid layout' },
  { id: 'preset-hero', label: 'Hero', icon: Star, presetKey: 'hero-section', description: 'Hero section layout' },
];

const SHAPE_TOOLS = [
  { id: 'shape-rect', label: 'Rect', icon: Square, description: 'Rectangle shape' },
  { id: 'shape-circle', label: 'Circle', icon: Circle, description: 'Circle/Ellipse shape' },
  { id: 'shape-triangle', label: 'Tri', icon: Triangle, description: 'Triangle shape' },
  { id: 'shape-star', label: 'Star', icon: Star, description: 'Star shape' },
  { id: 'shape-hex', label: 'Hex', icon: Hexagon, description: 'Hexagon shape' },
  { id: 'shape-draw', label: 'Draw', icon: PenTool, description: 'Freehand drawing' },
];

// Card-style tool tile used across every section of the Insert panel.
const ToolCard = ({ tool, onClick, isActive = false, lightMode = false, iconSize = 20 }) => {
  const Icon = tool.icon;
  const iconColor = isActive
    ? 'text-white'
    : tool.color || (lightMode ? 'text-gray-600' : 'text-gray-300');
  const labelColor = isActive
    ? 'text-white'
    : lightMode
      ? 'text-gray-700'
      : 'text-gray-200';

  return (
    <button
      type="button"
      onClick={() => onClick(tool)}
      title={tool.description || tool.label}
      style={tool.span === 2 ? { gridColumn: 'span 2 / span 2' } : undefined}
      className={`group flex flex-col items-center justify-center gap-1.5 rounded-xl border p-3 transition-all min-h-[68px] ${
        isActive
          ? 'border-blue-500 bg-blue-600/25 shadow-lg shadow-blue-600/20'
          : lightMode
            ? 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-white hover:shadow-sm'
            : 'border-gray-700/60 bg-gray-800/40 hover:border-gray-600 hover:bg-gray-800'
      }`}
    >
      <Icon size={iconSize} strokeWidth={isActive ? 2.25 : 1.75} className={`${iconColor} transition-colors`} />
      <span className={`text-[11px] font-medium leading-none ${labelColor}`}>{tool.label}</span>
    </button>
  );
};

// Collapsible section heading. When `collapsible` is false it renders as a
// static category label (used for the always-open BASIC group).
const SectionHeader = ({ label, isExpanded, onToggle, collapsible = true, lightMode = false }) => {
  const labelClasses = `text-[10px] uppercase tracking-[0.15em] font-bold ${
    lightMode ? 'text-blue-600' : 'text-gray-500'
  }`;

  if (!collapsible) {
    return <div className={`px-1 pt-1 pb-2 ${labelClasses}`}>{label}</div>;
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center justify-between px-1 py-2.5 transition-colors ${
        lightMode ? 'hover:text-blue-800' : 'hover:text-gray-300'
      }`}
    >
      <span className={labelClasses}>{label}</span>
      <ChevronDown
        size={14}
        className={`transition-transform ${isExpanded ? 'rotate-180' : ''} ${lightMode ? 'text-gray-400' : 'text-gray-500'}`}
      />
    </button>
  );
};

const ToolbarDrawer = ({
  isOpen,
  onToggle,
  // Content block handlers
  onAddContent,
  onApplyPresetLayout,
  onOpenPresetColumnsModal,
  onOpenInteractiveTools,
  // Slide context
  currentSectionId,
  isEditMode,
  topOffset = '42px',
  // Raise above the full-screen overlay so the drawer + edge arrow stay usable
  elevated = false,
  // (alignToggleTop removed: the edge toggle now sits at the top in every
  // mode, which is what the left drawer's arrow does, so there is nothing left
  // to opt into.)
  // Drop the edge toggle entirely — full screen uses this to clear every
  // overlay control off the slide. The ] shortcut still opens the drawer.
  hideToggle = false,
  // Feedback
  lastAction,
  // Theme
  lightMode = false,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    content: true,
    callouts: false,
    presets: false,
    shapes: false,
  });

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const handleAddBlock = useCallback(
    (tool) => {
      if (onAddContent && currentSectionId) {
        onAddContent(currentSectionId, tool.action, { forceModal: true, source: 'toolbar' });
      }
    },
    [onAddContent, currentSectionId]
  );

  const handlePresetClick = useCallback(
    (tool) => {
      const columnPresetKeys = new Set(['single-column', 'two-columns', 'three-columns']);
      if (columnPresetKeys.has(tool.presetKey) && onOpenPresetColumnsModal && currentSectionId) {
        onOpenPresetColumnsModal(currentSectionId, tool.presetKey);
        return;
      }

      if (onApplyPresetLayout) {
        onApplyPresetLayout(tool.presetKey);
      }
    },
    [onApplyPresetLayout, onOpenPresetColumnsModal, currentSectionId]
  );

  const handleShapeClick = useCallback(
    () => {
      if (onOpenInteractiveTools) {
        onOpenInteractiveTools();
      }
    },
    [onOpenInteractiveTools]
  );

  return (
    <>
    <div
      className={`fixed inset-y-0 right-0 ${elevated ? 'z-[10000]' : 'z-30'} no-print transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ top: topOffset }}
    >
      <div className={`h-full w-72 border-l flex flex-col shadow-xl overflow-hidden ${
        lightMode ? 'bg-white border-gray-200' : 'bg-gray-900 border-gray-800'
      }`}>
        {/* Panel header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${lightMode ? 'border-gray-200' : 'border-gray-800'}`}>
          <div className="flex items-center gap-2.5">
            <h2 className={`text-sm font-semibold ${lightMode ? 'text-gray-900' : 'text-white'}`}>Insert</h2>
            <span
              className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                isEditMode
                  ? (lightMode ? 'bg-emerald-100 text-emerald-700' : 'bg-emerald-900/50 text-emerald-400')
                  : (lightMode ? 'bg-amber-100 text-amber-700' : 'bg-amber-900/50 text-amber-400')
              }`}
              title={isEditMode ? 'Edit mode' : 'View mode'}
            >
              {isEditMode ? 'Edit' : 'View'}
            </span>
          </div>
          <button
            type="button"
            onClick={onToggle}
            className={`p-1 rounded-md transition-colors ${
              lightMode ? 'text-gray-400 hover:text-gray-900 hover:bg-gray-100' : 'text-gray-500 hover:text-white hover:bg-gray-800'
            }`}
            title="Collapse panel"
          >
            <ChevronsRight size={18} />
          </button>
        </div>

        {/* Hotkey hint */}
        <div className={`flex items-center gap-1.5 px-4 py-1.5 border-b text-[11px] ${lightMode ? 'border-gray-200 text-gray-500' : 'border-gray-800 text-gray-400'}`}>
          <span>Toggle this panel with</span>
          <kbd className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold ${lightMode ? 'border-gray-300 bg-gray-100 text-gray-700' : 'border-gray-700 bg-gray-800 text-gray-200'}`}>]</kbd>
        </div>

        {/* Scrollable tool area */}
        <div className="flex-1 overflow-y-auto px-3 py-2 scrollbar-thin">
          {/* --- BASIC / CONTENT BLOCKS --- */}
          <SectionHeader label="Basic" collapsible={false} lightMode={lightMode} />
          <div className="grid grid-cols-3 gap-2 pb-1">
            {CONTENT_TOOLS.map((tool) => (
              <ToolCard key={tool.id} tool={tool} onClick={handleAddBlock} lightMode={lightMode} />
            ))}
          </div>

          {/* --- CALLOUT BOXES --- */}
          <SectionHeader
            label="Callouts"
            isExpanded={expandedSections.callouts}
            onToggle={() => toggleSection('callouts')}
            lightMode={lightMode}
          />
          {expandedSections.callouts && (
            <div className="grid grid-cols-3 gap-2 pb-1">
              {CALLOUT_TOOLS.map((tool) => (
                <ToolCard key={tool.id} tool={tool} onClick={handleAddBlock} lightMode={lightMode} />
              ))}
            </div>
          )}

          {/* --- PRESET LAYOUTS --- */}
          <SectionHeader
            label="Presets"
            isExpanded={expandedSections.presets}
            onToggle={() => toggleSection('presets')}
            lightMode={lightMode}
          />
          {expandedSections.presets && (
            <div className="grid grid-cols-3 gap-2 pb-1">
              {PRESET_TOOLS.map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={{ ...tool, color: lightMode ? 'text-sky-600' : 'text-sky-400' }}
                  onClick={handlePresetClick}
                  lightMode={lightMode}
                />
              ))}
            </div>
          )}

          {/* --- SHAPES / INTERACTIVE --- */}
          <SectionHeader
            label="Draw-overs"
            isExpanded={expandedSections.shapes}
            onToggle={() => toggleSection('shapes')}
            lightMode={lightMode}
          />
          {expandedSections.shapes && (
            <div className="pb-1">
              <div className="grid grid-cols-3 gap-2">
                {SHAPE_TOOLS.map((tool) => (
                  <ToolCard
                    key={tool.id}
                    tool={{ ...tool, color: lightMode ? 'text-violet-600' : 'text-violet-400' }}
                    onClick={handleShapeClick}
                    lightMode={lightMode}
                  />
                ))}
              </div>
              {/* Interactive builder button */}
              <button
                type="button"
                onClick={onOpenInteractiveTools}
                className={`w-full flex items-center justify-center gap-2 p-2.5 mt-2 rounded-xl transition-all border border-dashed ${
                  lightMode
                    ? 'text-violet-600 hover:text-violet-800 hover:bg-violet-100 border-violet-300'
                    : 'text-violet-300 hover:text-white hover:bg-violet-700 border-violet-700'
                }`}
                title="Open full shape builder"
              >
                <Sparkles size={16} strokeWidth={1.75} />
                <span className="text-[11px] font-medium">Shape Builder</span>
              </button>
            </div>
          )}
        </div>

        {/* Bottom: Quick add text */}
        <div className={`border-t px-3 py-3 ${lightMode ? 'border-gray-200' : 'border-gray-800'}`}>
          <button
            type="button"
            onClick={() => onAddContent && currentSectionId && onAddContent(currentSectionId, 'text', { forceModal: true, source: 'toolbar' })}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white transition-all text-sm font-semibold shadow-lg shadow-blue-600/20"
            title="Quick add text block"
          >
            <Plus size={16} />
            <span>Text</span>
          </button>
        </div>

        {/* Action feedback toast */}
        {lastAction && (
          <div className="absolute bottom-16 left-3 right-3 bg-emerald-600 text-white text-[11px] font-medium text-center py-2 px-2 rounded-lg shadow-lg animate-pulse">
            {lastAction}
          </div>
        )}
      </div>

    </div>

      {/* Edge toggle tab — kept in its own layer that does NOT slide with the
          panel, so the target stays a comfortably large, easy-to-hit tab
          on-screen whether the panel is open or collapsed. */}
      <div
        className={`fixed inset-y-0 right-0 ${elevated ? 'z-[10001]' : 'z-[70]'} no-print pointer-events-none`}
        style={{ top: topOffset }}
      >
        <button
          type="button"
          onClick={onToggle}
          className={`pointer-events-auto absolute w-9 flex-col gap-1 items-center justify-center rounded-l-lg border border-r-0 shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            hideToggle ? 'hidden' : 'flex'
          } ${
            // Pinned to the top, matching the Sections tab on the left edge.
            // Both edge layers start at the same offset below the header, so
            // the same top/height puts the two tabs on one line.
            isOpen ? 'top-10 h-14' : 'top-10 h-28'
          } ${
            lightMode
              ? 'bg-white border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              : 'bg-gray-900 border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800'
          }`}
          style={{ right: isOpen ? '288px' : '0px' }}
          title={isOpen ? 'Collapse panel  ( ] )' : 'Open the Tools panel  ( ] )'}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse tools panel' : 'Expand tools panel'}
        >
          {isOpen ? (
            <ChevronRight size={18} />
          ) : (
            <>
              <ChevronLeft size={18} />
              {/* The panel now starts closed, so the collapsed tab has to say
                  what is behind it — a bare chevron reads as decoration. */}
              <span
                className={`text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${
                  lightMode ? 'text-gray-500' : 'text-gray-400'
                }`}
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              >
                Tools panel
              </span>
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default ToolbarDrawer;
